import type { EventTrackingAction, EventTrackingCategory } from '../declarations/EventTracking';
import { globalObservability } from '../global';
import { getDeployEnv } from './deployEnv';
import { isQuotaExceededError } from './isQuotaExceededError';
import segment from './segment';

const LOG_LEVEL_CONFIG_NAME = 'opendoor_log_level';

const log = (..._args: any[]) => {
  if (
    typeof window !== 'undefined' &&
    window.localStorage.getItem(LOG_LEVEL_CONFIG_NAME) === 'true'
  ) {
    console.log(..._args);
  }
};

/* eslint-disable */
// Linter doesn't like the way we're just calling functions without defining them ourselves
declare var ga: any;
declare var window: any;

const wrapQuotaError = (cb: any) => {
  try {
    cb();
  } catch (e) {
    if (!isQuotaExceededError(e)) {
      globalObservability.getSentryClient().captureException?.(e);
    }
  }
};

export const register = (traits: any) => {
  if (typeof ga === 'function' && typeof traits === 'object') {
    ga('set', traits);
  }
  segment.identify(traits);
  if (getDeployEnv() !== 'production') {
    log(`[DEBUG] Analytics.register: \n${JSON.stringify(traits, null, 2)}`);
  }
};

// use this for logged on users, so we don't need to use ajs anonymous id
export const registerWithUserID = (userId: string, traits: any) => {
  if (typeof ga === 'function' && typeof traits === 'object') {
    ga('set', traits);
  }
  segment.identify(userId, traits);
  if (getDeployEnv() !== 'production') {
    log(`[DEBUG] Analytics.register: \n${userId} ${JSON.stringify(traits, null, 2)}`);
  }
};

// As of 6/26/23, Segment's js library parses "search" data as a string and any tracking
// call that passes an object for "search" silently fails, so this typing makes sure that if
// we pass "search" it's a string. See: https://opendoor.atlassian.net/browse/CPLAT-2328
export type SegmentCompatibleData<T> = Omit<T, 'search'> & { search?: string };

/**
 * @deprecated use observability instead
 */
export const trackEvent = <AppCategory, AppActions>(
  category: EventTrackingCategory<AppCategory>,
  action: EventTrackingAction<AppActions>,
  value?: string,
  extra?: SegmentCompatibleData<Record<string, any>>,
) => {
  // Have to copy-paste the try/catch because doing try/catch functionally
  // with generators is complicated
  wrapQuotaError(() => {
    // https://developers.google.com/analytics/devguides/collection/analyticsjs/events
    if (typeof ga === 'function') {
      ga('send', 'event', category, action, value, extra);
    }
  });

  wrapQuotaError(() => {
    // https://segment.com/docs/spec/track/
    segment.track(`${category}-${action}`, {
      channel: 'web',
      domain: window.location.host,
      screen: {
        height: window.screen.height,
        width: window.screen.width,
      },
      value,
      ...extra,
    });
  });

  wrapQuotaError(() => {
    // https://support.google.com/tagmanager/answer/6106961?hl=en
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: `${category}.${action}.${value}`, extra });
  });

  if (getDeployEnv() !== 'production') {
    log(
      `[DEBUG] Analytics.trackEvent: \n${category}-${action} ${JSON.stringify(
        {
          value,
          ...extra,
        },
        null,
        2,
      )}`,
    );
  }
};

const matchInAppQueryParam = (queryParams: string) => {
  const fragments = queryParams && queryParams.match(/app\=(\d+)/);
  if (!fragments) {
    return false;
  }
  return !!fragments[1];
};

/**
 * @deprecated use observability instead
 */
export const trackPageView = (extra: Record<string, unknown>) => {
  const location: Location = window.location;
  const pathname = location.pathname;
  const search = location.search;
  trackEvent('page', 'view', 'page', {
    app: matchInAppQueryParam(search),
    pathname,
    search,
    ...extra,
  });
};
